import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { validatedApi } from '../../../lib/api/validated-client';
import type {
  FeatureFlagResponse,
  FeatureFlagCheckResponse,
  CreateFeatureFlagRequest,
  UpdateFeatureFlagRequest,
} from '@lov/shared';

/**
 * Check if a single feature flag is enabled (public, cached)
 * Use this in components to conditionally render features
 */
export function useFeatureFlag(key: string) {
  return useQuery<FeatureFlagCheckResponse>({
    queryKey: ['feature-flag', key],
    queryFn: async () => {
      return validatedApi.get(`/feature-flags/check/${key}`);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - flags don't change often
    enabled: !!key,
    // Return false by default if flag doesn't exist or fails
    placeholderData: { key, enabled: false },
  });
}

/**
 * Get all feature flags (admin only)
 */
export function useFeatureFlags() {
  return useQuery<FeatureFlagResponse[]>({
    queryKey: ['feature-flags'],
    queryFn: async () => {
      return validatedApi.get('/feature-flags');
    },
    staleTime: 1000 * 30, // 30 seconds - admin may see updates
  });
}

/**
 * Get a single feature flag by key (admin only)
 */
export function useFeatureFlagDetails(key: string) {
  return useQuery<FeatureFlagResponse>({
    queryKey: ['feature-flags', key],
    queryFn: async () => {
      return validatedApi.get(`/feature-flags/${key}`);
    },
    enabled: !!key,
  });
}

/**
 * Create a new feature flag (admin only)
 */
export function useCreateFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (flagData: CreateFeatureFlagRequest) => {
      return validatedApi.post('/feature-flags', flagData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
    },
  });
}

/**
 * Update a feature flag (admin only)
 */
export function useUpdateFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, data }: { key: string; data: UpdateFeatureFlagRequest }) => {
      return validatedApi.put(`/feature-flags/${key}`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flags', variables.key] });
      queryClient.invalidateQueries({ queryKey: ['feature-flag', variables.key] });
    },
  });
}

/**
 * Toggle a feature flag (admin only)
 */
export function useToggleFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (key: string) => {
      return validatedApi.put(`/feature-flags/${key}/toggle`);
    },
    // Optimistic update for instant UI feedback
    onMutate: async (key) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['feature-flags'] });

      // Snapshot the previous value
      const previousFlags = queryClient.getQueryData<FeatureFlagResponse[]>(['feature-flags']);

      // Optimistically update
      if (previousFlags) {
        queryClient.setQueryData<FeatureFlagResponse[]>(
          ['feature-flags'],
          previousFlags.map((flag) =>
            flag.key === key ? { ...flag, enabled: !flag.enabled } : flag
          )
        );
      }

      return { previousFlags };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousFlags) {
        queryClient.setQueryData(['feature-flags'], context.previousFlags);
      }
    },
    onSettled: (_, __, key) => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flag', key] });
    },
  });
}

/**
 * Delete a feature flag (admin only)
 */
export function useDeleteFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (key: string) => {
      await validatedApi.delete(`/feature-flags/${key}`);
    },
    onSuccess: (_, key) => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.removeQueries({ queryKey: ['feature-flags', key] });
      queryClient.removeQueries({ queryKey: ['feature-flag', key] });
    },
  });
}

/**
 * Trigger emergency kill switch (admin only)
 */
export function useTriggerKillSwitch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, reason }: { key: string; reason: string }) => {
      return validatedApi.post(`/feature-flags/${key}/kill-switch`, { reason });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flags', variables.key] });
      queryClient.invalidateQueries({ queryKey: ['feature-flag', variables.key] });
    },
  });
}

/**
 * Clear emergency kill switch (admin only)
 */
export function useClearKillSwitch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (key: string) => {
      return validatedApi.delete(`/feature-flags/${key}/kill-switch`);
    },
    onSuccess: (_, key) => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flags', key] });
      queryClient.invalidateQueries({ queryKey: ['feature-flag', key] });
    },
  });
}
